
import React from "react";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Clock, Heart, Copy } from "lucide-react";

interface QuickFilter {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  type: 'sort' | 'timeFilter';
}

interface QuickFiltersProps {
  selectedSort: string;
  selectedTimeFilter: string;
  onSortChange: (sort: string) => void;
  onTimeFilterChange: (timeFilter: string) => void;
  disabled?: boolean;
}

const quickFilters: QuickFilter[] = [
  { id: 'trending', label: 'Trending', icon: TrendingUp, value: 'popular', type: 'sort' },
  { id: 'recent', label: 'Recent', icon: Clock, value: 'recent', type: 'sort' },
  { id: 'most-liked', label: 'Most Liked', icon: Heart, value: 'likes', type: 'sort' },
  { id: 'most-copied', label: 'Most Copied', icon: Copy, value: 'copies', type: 'sort' },
];

const timeFilters: QuickFilter[] = [
  { id: 'all-time', label: 'All Time', icon: Clock, value: '', type: 'timeFilter' },
  { id: 'this-week', label: 'This Week', icon: Clock, value: 'week', type: 'timeFilter' },
  { id: 'this-month', label: 'This Month', icon: Clock, value: 'month', type: 'timeFilter' },
];

const QuickFilters: React.FC<QuickFiltersProps> = ({
  selectedSort,
  selectedTimeFilter,
  onSortChange,
  onTimeFilterChange,
  disabled = false
}) => {
  const handleFilterClick = (filter: QuickFilter) => {
    if (disabled) return;
    
    if (filter.type === 'sort') {
      // If the filter is already selected, reset to default 'relevance'
      if (selectedSort === filter.value) {
        onSortChange('relevance');
      } else {
        onSortChange(filter.value);
      }
    } else if (filter.type === 'timeFilter') {
      // If the time filter is already selected, reset to empty (all time)
      if (selectedTimeFilter === filter.value) {
        onTimeFilterChange('');
      } else {
        onTimeFilterChange(filter.value);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Sort Filters */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-2">Sort by</h3>
        <div className="flex flex-wrap gap-2">
          {quickFilters.map(filter => {
            const Icon = filter.icon;
            const isSelected = selectedSort === filter.value;
            
            return (
              <Badge
                key={filter.id}
                variant={isSelected ? "default" : "outline"}
                className={`px-3 py-1 cursor-pointer hover:scale-105 transition-transform flex items-center gap-1 ${disabled ? 'opacity-70' : ''}`}
                onClick={() => handleFilterClick(filter)}
              >
                <Icon className="h-3 w-3" />
                {filter.label}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Time Filters */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-2">Time period</h3>
        <div className="flex flex-wrap gap-2">
          {timeFilters.map(filter => {
            const Icon = filter.icon;
            const isSelected = selectedTimeFilter === filter.value;
            
            return (
              <Badge
                key={filter.id}
                variant={isSelected ? "default" : "outline"}
                className={`px-3 py-1 cursor-pointer hover:scale-105 transition-transform flex items-center gap-1 ${disabled ? 'opacity-70' : ''}`}
                onClick={() => handleFilterClick(filter)}
              >
                <Icon className="h-3 w-3" />
                {filter.label}
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default QuickFilters;
